/**
 * PATSettings - Personal Access Token configuration UI
 */

import { useState } from "react";
import { useGitStore } from "@/stores/gitStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Key,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Trash2,
  ExternalLink,
} from "lucide-react";

interface PATSettingsProps {
  workspaceId: number;
  onConnect?: () => void;
}

export function PATSettings({ workspaceId, onConnect }: PATSettingsProps) {
  const {
    workspacePAT,
    patValidating,
    patError,
    isSaving,
    validatePAT,
    savePAT,
    deletePAT,
  } = useGitStore();

  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    login?: string;
    scopes: string[];
  } | null>(null);

  const handleValidate = async () => {
    if (!token.trim()) return;

    // Clear previous validation result before starting
    setValidationResult(null);

    try {
      const result = await validatePAT(token.trim());
      if (result) {
        setValidationResult({
          valid: result.valid,
          login: result.githubLogin,
          scopes: result.scopes,
        });
      }
      // If result is null, patError will be set in the store and shown via the error display below
    } catch (error) {
      // This shouldn't happen as store catches errors, but just in case
      console.error("PAT validation error:", error);
    }
  };

  const handleSave = async () => {
    if (!token.trim()) return;

    const success = await savePAT(workspaceId, token.trim());
    if (success) {
      setToken("");
      setValidationResult(null);
      onConnect?.();
    }
  };

  const handleDelete = async () => {
    await deletePAT(workspaceId);
    setToken("");
    setValidationResult(null);
  };

  // If PAT already exists, show info and remove option
  if (workspacePAT) {
    return (
      <div className="space-y-4">
        <div className="p-4 border rounded-lg bg-muted/50">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-500/10">
                <Key className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium">Personal Access Token</p>
                <p className="text-sm text-muted-foreground">
                  {workspacePAT.githubLogin && (
                    <span className="mr-2">@{workspacePAT.githubLogin}</span>
                  )}
                  {workspacePAT.tokenHint && (
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">
                      {workspacePAT.tokenHint}
                    </code>
                  )}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="text-green-600 border-green-600">
              <CheckCircle className="w-3 h-3 mr-1" />
              Connected
            </Badge>
          </div>

          {workspacePAT.scopes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {workspacePAT.scopes.map((scope) => (
                <Badge key={scope} variant="secondary" className="text-xs">
                  {scope}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleDelete}
          disabled={isSaving}
          className="text-destructive hover:text-destructive"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4 mr-2" />
          )}
          Remove Token
        </Button>
      </div>
    );
  }

  // Token input form
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pat-token">Personal Access Token</Label>
        <div className="relative">
          <Input
            id="pat-token"
            type={showToken ? "text" : "password"}
            placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
              setValidationResult(null);
            }}
            className="pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
            onClick={() => setShowToken(!showToken)}
            aria-label={showToken ? "Hide token" : "Show token"}
          >
            {showToken ? (
              <EyeOff className="w-4 h-4 text-muted-foreground" />
            ) : (
              <Eye className="w-4 h-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Create a{" "}
          <a
            href="https://github.com/settings/tokens/new?description=Colony%20App&scopes=repo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            classic token with repo scope
            <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </div>

      {/* Validation Result */}
      {validationResult && (
        <Alert variant={validationResult.valid ? "default" : "destructive"}>
          {validationResult.valid ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertDescription>
            {validationResult.valid ? (
              <div>
                <p className="font-medium text-green-600">Token valid</p>
                {validationResult.login && (
                  <p className="text-sm">Authenticated as @{validationResult.login}</p>
                )}
                {validationResult.scopes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {validationResult.scopes.map((scope) => (
                      <Badge key={scope} variant="secondary" className="text-xs">
                        {scope}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p>{patError || "Token validation failed"}</p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Error display */}
      {patError && !validationResult && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{patError}</AlertDescription>
        </Alert>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={handleValidate}
          disabled={!token.trim() || patValidating}
        >
          {patValidating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Validate
        </Button>
        <Button
          onClick={handleSave}
          disabled={!token.trim() || !validationResult?.valid || isSaving}
        >
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save & Connect
        </Button>
      </div>
    </div>
  );
}
